import { log } from '@/utils/logger';
import {
  getCoordinatesFromAddress,
  validateCoordinates,
  toMapLibreCoordinates
} from '@/utils/geocoding';

const RECENT_SEARCHES_KEY = 'recentSearches';
const logger = log.component('LocationSearch');

interface SearchResult {
  coordinates: {
    lat: number;
    lng: number;
  };
  mapLibreCoordinates: [number, number];
  displayName: string;
  locationType: string;
  boundingbox: [string, string, string, string];
}

// Helper to determine zoom level based on location type
const getLocationSettings = (locationType: string): { zoom: number; padding: number } => {
  switch (locationType) {
    case 'country':
      return { zoom: 5, padding: 0.5 };
    case 'state':
    case 'province':
    case 'region':
      return { zoom: 7, padding: 0.3 };
    case 'county':
      return { zoom: 8, padding: 0.2 };
    case 'city':
    case 'town':
      return { zoom: 11, padding: 0.05 };
    case 'village':
    case 'hamlet':
    case 'suburb':
      return { zoom: 13, padding: 0.02 };
    case 'attraction':
    case 'natural':
    case 'building':
      return { zoom: 15, padding: 0.005 };
    default:
      return { zoom: 9, padding: 0.1 };
  }
};

export function useLocationSearch() {
  const searchLocation = async (query: string): Promise<SearchResult | null> => {
    if (!query.trim()) return null;

    try {
      const geocodingResult = await getCoordinatesFromAddress(query);
      const { lat, lng, display_name, locationType, boundingbox } = geocodingResult;

      const coordinates = { lat, lng };
      if (!validateCoordinates(coordinates)) {
        logger.error('Invalid coordinates received from geocoding', coordinates);
        throw new Error('Invalid coordinates received from geocoding');
      }

      const mapLibreCoordinates = toMapLibreCoordinates(coordinates);
      updateSearchHistory(query);

      return {
        coordinates,
        mapLibreCoordinates,
        displayName: display_name,
        locationType,
        boundingbox
      };
    } catch (error) {
      logger.error('Search error', error);
      return null;
    }
  };

  const updateSearchHistory = (query: string) => {
    const recentSearches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify([...new Set([query, ...recentSearches])].slice(0, 5))
    );
  };

  return { searchLocation };
}